import { useMemo } from 'react';
import useIsKarmaEarnShown from 'hooks/useIsKarmaEarnShown';

const POINTS_MULTIPLIER = 1000;

/**
 * Parses dollar-like input (1.10 | "$1.10" | "1.10$" | "1,10" | "1.10") to a number.
 * @param {number|string} value
 * @returns {number}
 */
function parseDollarValue(value) {
  if (value == null || value === '') {
    return 0;
  }
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }
  const str = String(value).replace(/\s/g, '').replace(/\$/g, '').replace(',', '.');
  const parsed = parseFloat(str);

  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Builds dollar result object.
 * @param {number} parsed
 * @returns {{ isDollars: true, value: string, integerPart: string, fractionPart: string }}
 */
function formatDollars(parsed) {
  const value = parsed.toFixed(2);
  const [integerPart, fractionPart = '00'] = value.split('.');

  return {
    isDollars: true,
    value,
    integerPart,
    fractionPart,
  };
}

/**
 * Converts dollar amount to points (×1000) when user passes Karma Earn check
 * Accepts: 1.10 | "$1.10" | "1.10$" | "1,10" | "1.10"
 *
 * @param {number|string} value - Dollar amount in any of the accepted formats
 * @returns {{ isDollars: false, value: string } | { isDollars: true, value: string, integerPart: string, fractionPart: string }}
 *   Points: { isDollars: false, value: '1,100' }
 *   Dollars: { isDollars: true, value: '1.10', integerPart: '1', fractionPart: '10' } or value: '-1.10', integerPart: '-1', fractionPart: '10'
 */
function useConvertDollarsToPoints(value) {
  const isKarmaEarnShown = useIsKarmaEarnShown();

  return useMemo(() => {
    const parsed = parseDollarValue(value);

    if (!isKarmaEarnShown) {
      return formatDollars(parsed);
    }

    const points = Math.round(parsed * POINTS_MULTIPLIER);

    return { isDollars: false, value: points.toLocaleString() };
  }, [value, isKarmaEarnShown]);
}

export default useConvertDollarsToPoints;
